// StyleGuide.jsx — living design-system reference for Rekonsil. Internal route
// (/styleguide); not in the customer nav. It dogfoods the real tokens, classes and
// the shared status map (../constants/statementStatus) so the guide can never drift
// from the app. Rule of thumb captured here: PILLS ARE FOR CONTROLS, STATUS IS AN ICON.
import React from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Row, Col, Button, Form } from "react-bootstrap";
import { FaEquals, FaNotEqual, FaTimes, FaCheckCircle, FaTimesCircle, FaRegClock } from "react-icons/fa";
import { STATEMENT_STATUS } from "../constants/statementStatus";

// ---- small building blocks for the guide itself --------------------------------
const Section = ({ id, title, intro, children }) => (
   <section id={id} className="sg-section">
      <h3 className="sg-section-title">{title}</h3>
      {intro && <p className="sg-section-intro">{intro}</p>}
      {children}
   </section>
);

const Specimen = ({ label, code, children }) => (
   <div className="sg-specimen">
      <div className="sg-specimen-stage">{children}</div>
      <div className="sg-specimen-meta">
         <span className="sg-specimen-label">{label}</span>
         {code && <code className="sg-token">{code}</code>}
      </div>
   </div>
);

const Swatch = ({ name, hex, token, dark }) => (
   <div className="sg-swatch">
      <div className="sg-swatch-chip" style={{ background: hex, color: dark ? "#fff" : "#212830" }}>{hex}</div>
      <div className="sg-swatch-name">{name}</div>
      <code className="sg-token">{token}</code>
   </div>
);

const STATUS_ORDER = ["reconciled", "mostly", "partial", "pending", "blocked"];

export default function StyleGuide() {
   return (
      <>
         <Header />
         <div className="main main-app p-3 p-lg-4 p-xxl-5 styleguide-page">
            <Row className="g-3 g-lg-4 align-items-start">
               <Col>
                  <div className="d-flex align-items-center gap-2">
                     <h2 className="main-title fs-4 mb-1">Design System</h2>
                     <span className="payee-count-chip">reference</span>
                  </div>
                  <p className="page-subtitle">
                     The single source of truth for Rekonsil&rsquo;s UI. New screens compose from
                     these — measure here before adding anything. The Suppliers and Invoice
                     Statements pages are the benchmark this guide encodes.
                  </p>
               </Col>
            </Row>

            {/* RULE CALLOUT ---------------------------------------------------- */}
            <div className="sg-rule">
               <strong>The rule:</strong> a <em>pill</em> shape signals an interactive control
               (button, filter chip, toggle). <em>Status</em> is communicated with an icon &mdash;
               never a pill. Weights are 400 / 600 only. No uppercase microcaps.
            </div>

            {/* FOUNDATIONS — COLOR -------------------------------------------- */}
            <Section id="color" title="Color" intro="Indigo is the one accent; everything else is a soft grey. Color is reserved for status and brand moments, not decoration.">
               <div className="sg-label">Brand</div>
               <div className="sg-swatch-grid">
                  <Swatch name="Primary (indigo)" hex="#506fd9" token="$color-ui-01" dark />
                  <Swatch name="Primary soft" hex="#85b6ff" token="$color-ui-02" />
                  <Swatch name="Record link" hex="#0073bb" token="link" dark />
               </div>
               <div className="sg-label">Status</div>
               <div className="sg-swatch-grid">
                  <Swatch name="Success / reconciled" hex="#198754" token="$success" dark />
                  <Swatch name="Warning / partial" hex="#fd7e14" token="warning" dark />
                  <Swatch name="Danger / blocked" hex="#dc3545" token="$danger" dark />
                  <Swatch name="Neutral / pending" hex="#6c757d" token="$gray-600" dark />
               </div>
               <div className="sg-label">Greys</div>
               <div className="sg-swatch-grid">
                  <Swatch name="Canvas" hex="#f8f9fc" token="$gray-100" />
                  <Swatch name="Hover fill" hex="#f3f5f9" token="$gray-200" />
                  <Swatch name="Border" hex="#e2e5ec" token="$gray-300" />
                  <Swatch name="Muted text" hex="#9ea7b1" token="$gray-500" dark />
                  <Swatch name="Body text" hex="#41505f" token="$gray-700" dark />
                  <Swatch name="Headings" hex="#212830" token="$gray-900" dark />
               </div>
            </Section>

            {/* TYPOGRAPHY ------------------------------------------------------ */}
            <Section id="type" title="Typography" intro="System font stack. The Documents table sets the bar: quiet, dense, legible.">
               <Specimen label="Page title" code="h2.main-title.fs-4"><h2 className="main-title fs-4 mb-0">Invoice Statements</h2></Specimen>
               <Specimen label="Subtitle" code=".page-subtitle"><p className="page-subtitle mb-0">Who your statements get paid to.</p></Specimen>
               <Specimen label="Table header" code="th · 13px / 600"><span style={{ fontSize: 13, fontWeight: 600, color: "#212830" }}>Statement date</span></Specimen>
               <Specimen label="Table cell" code="td · 14px / 400"><span style={{ fontSize: 14, fontWeight: 400, color: "#212830" }}>2026-06-01</span></Specimen>
               <Specimen label="Record link" code="#0073bb / 400"><a href="#" onClick={(e) => e.preventDefault()} style={{ color: "#0073bb", textDecoration: "none" }}>Carquest Auto Parts</a></Specimen>
               <Specimen label="Small / meta" code="$font-size-sm · 13px"><span style={{ fontSize: "0.8125rem", color: "#6e7985" }}>150 / 156 matched</span></Specimen>
            </Section>

            {/* BUTTONS --------------------------------------------------------- */}
            <Section id="buttons" title="Buttons" intro="Five roles. Primary is the only solid-indigo; filters/toggles are the only pills. Retire btn-dark and ad-hoc shapes.">
               <Specimen label="Primary — the main action (one per view)" code='btn-primary'>
                  <Button variant="primary">Reconcile</Button>
               </Specimen>
               <Specimen label="Secondary — supporting action" code='btn-outline-secondary'>
                  <Button variant="outline-secondary">View statement</Button>
               </Specimen>
               <Specimen label="Filter / toggle — the pill (a control)" code='btn-outline-secondary.rounded-pill'>
                  <button type="button" className="btn btn-outline-secondary rounded-pill">Clear Filters</button>
               </Specimen>
               <Specimen label="Subtle — tertiary / inline" code='btn-link'>
                  <Button variant="link" className="p-0" style={{ textDecoration: "none" }}>Add terms</Button>
               </Specimen>
               <Specimen label="Destructive — irreversible only" code='btn-outline-danger'>
                  <Button variant="outline-danger">Delete</Button>
               </Specimen>
               <Specimen label="Sizes" code='btn-sm · default'>
                  <span className="d-inline-flex gap-2 align-items-center">
                     <Button variant="primary" size="sm">Small</Button>
                     <Button variant="primary">Default</Button>
                  </span>
               </Specimen>
            </Section>

            {/* STATUS ---------------------------------------------------------- */}
            <Section id="status" title="Status" intro="Icons, not pills — color-coded, with a tooltip. Each semantic axis has ONE icon set, defined once in code and reused by every view.">
               <div className="sg-label">Reconciliation status — statement-level (from constants/statementStatus.js)</div>
               <div className="sg-status-row">
                  {STATUS_ORDER.map((key) => {
                     const m = STATEMENT_STATUS[key];
                     const Icon = m.icon;
                     return (
                        <div key={key} className="sg-status-item" title={m.title}>
                           <Icon color={m.color} size={20} />
                           <span>{m.label}</span>
                        </div>
                     );
                  })}
               </div>

               <div className="sg-label">Match status — reconcile line-level</div>
               <div className="sg-status-row">
                  <div className="sg-status-item"><FaEquals color="#198754" size={18} /><span>Full match</span></div>
                  <div className="sg-status-item"><FaNotEqual color="#fd7e14" size={18} /><span>Partial</span></div>
                  <div className="sg-status-item"><FaTimes color="#dc3545" size={18} /><span>Unmatched</span></div>
                  <div className="sg-status-item"><FaTimes color="#9ea7b1" size={18} /><span>No ERP entry</span></div>
               </div>

               <div className="sg-label">Document processing status &mdash; <em>recommended</em> (today it&rsquo;s plain black text; &ldquo;Failed&rdquo; should be impossible to miss)</div>
               <div className="sg-status-row">
                  <div className="sg-status-item"><FaCheckCircle color="#198754" size={18} /><span>Completed</span></div>
                  <div className="sg-status-item"><FaRegClock color="#506fd9" size={18} /><span>Processing</span></div>
                  <div className="sg-status-item"><FaTimesCircle color="#dc3545" size={18} /><span style={{ color: "#dc3545", fontWeight: 600 }}>Failed</span></div>
               </div>
            </Section>

            {/* BADGES & CHIPS -------------------------------------------------- */}
            <Section id="badges" title="Badges & chips" intro="Soft-tint, not solid fill. A badge labels a record's state; a chip carries a count. These are not buttons — no hover, no click.">
               <Specimen label="Payee state badge" code='.payee-badge'>
                  <span className="d-inline-flex gap-2">
                     <span className="payee-badge live">Payee</span>
                     <span className="payee-badge retired">Retired</span>
                     <span className="payee-none">&mdash;</span>
                  </span>
               </Specimen>
               <Specimen label="Count chip" code='.payee-count-chip'>
                  <span className="payee-count-chip">1,167 suppliers</span>
               </Specimen>
            </Section>

            {/* TABLE SHELL ----------------------------------------------------- */}
            <Section id="tables" title="Data tables" intro="Every data table lives in a white card on the canvas — never bare. Use the global .data-table-card shell (Suppliers, Statements, Documents).">
               <div className="data-table-card">
                  <table className="table mb-0">
                     <thead>
                        <tr>
                           <th>Supplier</th><th>Statement date</th><th className="text-end">Matched</th><th className="text-center">Status</th>
                        </tr>
                     </thead>
                     <tbody>
                        <tr>
                           <td><a href="#" className="supplier-name-link" onClick={(e) => e.preventDefault()}>Worldpac</a></td>
                           <td>2026-05-25</td><td className="text-end">48 / 48</td>
                           <td className="text-center"><FaCheckCircle color="#198754" size={18} /></td>
                        </tr>
                        <tr>
                           <td><a href="#" className="supplier-name-link" onClick={(e) => e.preventDefault()}>Carquest Auto Parts</a></td>
                           <td>2026-05-31</td><td className="text-end">150 / 156</td>
                           <td className="text-center">{(() => { const I = STATEMENT_STATUS.mostly.icon; return <I color={STATEMENT_STATUS.mostly.color} size={18} />; })()}</td>
                        </tr>
                     </tbody>
                  </table>
               </div>
            </Section>

            {/* PAGE HEADER PATTERN -------------------------------------------- */}
            <Section id="header" title="Page header" intro="Title + optional count chip on one line, a single-sentence subtitle beneath. Every screen opens this way.">
               <div className="sg-specimen-stage">
                  <div className="d-flex align-items-center gap-2">
                     <h2 className="main-title fs-4 mb-1">Documents</h2>
                     <span className="payee-count-chip">312</span>
                  </div>
                  <p className="page-subtitle mb-0">Every file you&rsquo;ve uploaded, with its processing status.</p>
               </div>
            </Section>

            {/* FORM CONTROLS --------------------------------------------------- */}
            <Section id="forms" title="Form controls" intro="Default Bootstrap sizing. Placeholders are human language — never raw column names like “Search sm_name”.">
               <Row className="g-3" style={{ maxWidth: 720 }}>
                  <Col sm={6}><Form.Label className="sg-form-label">Text input</Form.Label><Form.Control placeholder="Search supplier" /></Col>
                  <Col sm={6}><Form.Label className="sg-form-label">Select</Form.Label><Form.Select><option>All statuses</option><option>Reconciled</option></Form.Select></Col>
                  <Col sm={6}><Form.Label className="sg-form-label">Small input</Form.Label><Form.Control size="sm" placeholder="From" /></Col>
                  <Col sm={6}><Form.Label className="sg-form-label">Checkbox</Form.Label><Form.Check label="Show ignored lines" /></Col>
               </Row>
            </Section>

            {/* EMPTY STATE ----------------------------------------------------- */}
            <Section id="empty" title="Empty state" intro="Centered, calm, one line. Tell the user what they'd see here, not just “No data”.">
               <div className="data-table-card">
                  <div className="sg-empty">No statements match these filters yet.</div>
               </div>
            </Section>

            <Footer />
         </div>
      </>
   );
}
